<template>
<div class="pl-drawer-overlay" @click.self="!busy && $emit('close')">
    <aside class="pl-drawer">
        <div class="pl-drawer-hd">
            <span class="pl-drawer-wcb">Bulk move</span>
            <button type="button" class="pl-drawer-close" :disabled="busy" @click="$emit('close')">✕</button>
            <h3>Move {{ chips.length }} chip{{ chips.length === 1 ? '' : 's' }}</h3>
            <div class="pl-drawer-qln">{{ fmtSecs(totalSeconds) }} of work · {{ jobCount }} job{{ jobCount === 1 ? '' : 's' }}</div>
        </div>

        <div v-if="error || moveError" class="pl-drawer-error">{{ moveError || error }}</div>

        <!-- what's in hand -->
        <div class="pl-drawer-sec pl-bm-list">
            <div v-for="c in chips" :key="'bm' + c.id" class="pl-bm-row"
                 :class="{ 'pl-bm-row-refused': refusedById[c.id], 'pl-bm-row-unchanged': unchangedById[c.id] }"
                 :style="{ '--plwc': wc.colour(c.workcentre_id, c.type) }">
                <svg class="pl-i"><use :href="'#' + wc.icon(c.workcentre_id, c.type)"/></svg>
                <div class="pl-bm-cx">
                    <div class="pl-bm-r1">
                        <b>{{ c.pretty_name }}</b>
                        <span class="pl-bm-mt">{{ fmtSecs(c.planned_seconds) }}</span>
                    </div>
                    <div class="pl-bm-r2">
                        <template v-if="c.quote_id">Q{{ c.quote_id }} · {{ c.quote_name }} · </template>{{ personName(c.user_id) }} · {{ fmtDay(c.date) }}
                        <template v-if="movedById[c.id] && movedById[c.id].to.date !== c.date">
                            → <b>{{ fmtDay(movedById[c.id].to.date) }}</b>
                            <span v-if="movedById[c.id].backdated" class="pl-bm-past">past</span>
                        </template>
                    </div>
                    <div v-if="refusedById[c.id]" class="pl-bm-why">⚠ {{ refusedById[c.id].reason }}</div>
                    <div v-else-if="unchangedById[c.id]" class="pl-bm-why pl-bm-why-soft">already there</div>
                    <div v-else-if="movedById[c.id] && movedById[c.id].out_of_sequence" class="pl-bm-why">
                        ⚠ out of sequence, placed anyway — logged
                    </div>
                </div>
                <button type="button" class="pl-bm-x" title="Take this chip out of the selection" @click="$emit('remove', c.id)">×</button>
            </div>
        </div>

        <!-- WHEN -->
        <div class="pl-drawer-sec">
            <h4>When</h4>
            <label class="pl-bm-opt">
                <input type="radio" value="date" v-model="mode"> <span>Move to day</span>
                <input type="date" v-model="date" class="pl-dinput pl-bm-date" :disabled="mode !== 'date'" @focus="mode = 'date'">
            </label>
            <label class="pl-bm-opt">
                <input type="radio" value="days" v-model="mode"> <span>Shift by</span>
                <span class="pl-bm-quick">
                    <button v-for="q in quick" :key="'q' + q.n" type="button" class="pl-bm-q"
                            :class="{ on: mode === 'days' && days === q.n }" @click="setDays(q.n)">{{ q.label }}</button>
                    <input type="number" v-model.number="days" min="-60" max="60" class="pl-dinput pl-bm-days"
                           :disabled="mode !== 'days'" @focus="mode = 'days'" title="days, negative pulls earlier">
                </span>
            </label>
            <label class="pl-bm-opt">
                <input type="radio" value="keep" v-model="mode"> <span>Keep each day</span>
                <small class="pl-bm-sub">only change who does it</small>
            </label>
        </div>

        <!-- WHO -->
        <div class="pl-drawer-sec">
            <h4>Who</h4>
            <label class="pl-bm-opt">
                <input type="radio" value="keep" v-model="personMode"> <span>Keep each person</span>
            </label>
            <label class="pl-bm-opt">
                <input type="radio" value="set" v-model="personMode"> <span>Everyone to</span>
                <select v-model="userId" class="pl-dinput pl-bm-person" :disabled="personMode !== 'set'" @focus="personMode = 'set'">
                    <option :value="null">person…</option>
                    <option v-for="p in board.people" :key="'bp' + p.id" :value="p.id">{{ p.name }}</option>
                </select>
            </label>
        </div>

        <!-- PREVIEW (a real dry run of the same request) -->
        <div class="pl-drawer-sec">
            <h4>Preview</h4>
            <div v-if="!hasTarget" class="pl-dnote">Pick a day, a shift or a person to see what will move.</div>
            <div v-else-if="previewing && !preview" class="pl-bm-prev pl-bm-prev-soft">Checking…</div>
            <template v-else-if="preview">
                <div class="pl-bm-prev pl-bm-prev-ok" v-if="preview.moved.length">
                    ✓ <b>{{ preview.moved.length }}</b> will move<template v-if="targetLabel"> → {{ targetLabel }}</template>
                </div>
                <div class="pl-bm-prev pl-bm-prev-soft" v-if="preview.unchanged.length">
                    {{ preview.unchanged.length }} already there
                </div>
                <div class="pl-bm-prev pl-bm-prev-soft" v-if="pastCount">
                    {{ pastCount }} land{{ pastCount === 1 ? 's' : '' }} on a past day — the receipt will offer “mark done”
                </div>
                <div class="pl-bm-prev pl-bm-prev-warn" v-if="preview.refused.length">
                    ⚠ <b>{{ preview.refused.length }}</b> refused — reasons are on the chips above
                </div>
                <label v-if="oosCount" class="pl-bm-force">
                    <input type="checkbox" v-model="allowOos">
                    Place the {{ oosCount }} out-of-sequence chip{{ oosCount === 1 ? '' : 's' }} anyway — logged as a deliberate exception
                </label>
                <div v-if="!preview.moved.length && !preview.refused.length && !preview.unchanged.length" class="pl-dnote">
                    Nothing to move.
                </div>
            </template>
        </div>

        <div class="pl-drawer-sec">
            <button type="button" class="pl-dbtn pl-dbtn-dark" :disabled="busy || !canMove" @click="doMove">
                {{ busy ? 'Moving…' : ('Move ' + (preview ? preview.moved.length : chips.length) + ' chip' + ((preview ? preview.moved.length : chips.length) === 1 ? '' : 's')) }}
            </button>
            <div class="pl-dnote pl-bm-undo-note">You get an Undo on the receipt — it puts every chip back exactly where it was.</div>
        </div>
    </aside>
</div>
</template>

<script>
import { fmtSecs, fmtDay, errMsg, wcLookups } from './shared';

/**
 * The multi-select side panel (planning/planner-bulk-move-design.md §2.3).
 * One uniform target for the whole selection; every change re-runs the
 * server's dry run so Frances sees, by name and reason, what will move
 * and what won't BEFORE she commits — the drawer's warn-then-force made
 * visible instead of a native confirm().
 *
 * The REAL move is not sent from here: the panel emits `move(payload,
 * label)` and PlannerPage owns the request. A panel that closes while
 * its own request is in flight is destroyed with it — the server has
 * moved the chips but the receipt, the Undo and the board refresh are
 * lost (seen on the VM 2026-09-08). The parent outlives the panel, and
 * passes `busy` back so the panel can't be dismissed mid-move.
 */
export default {
    name: 'BulkMoveDrawer',

    props: {
        chips: { type: Array, required: true },
        board: { type: Object, required: true },
        permissions: { type: Object, required: true },
        busy: { type: Boolean, default: false },      // parent's move request in flight
        moveError: { type: String, default: null },  // parent's move request failed
        // A target pre-loaded by a refused People-board drop — { user_id?, days?,
        // scheduled_date? } — so the panel opens already showing why.
        preset: { type: Object, default: null },
    },

    data() {
        return {
            mode: 'date',          // 'date' | 'days' | 'keep'
            date: '',
            days: 7,
            personMode: 'keep',    // 'keep' | 'set'
            userId: null,
            allowOos: false,
            preview: null,         // last dry-run response
            previewing: false,
            error: null,
            quick: [
                { n: 1, label: '+1 day' },
                { n: 7, label: '+1 week' },
                { n: 14, label: '+2 weeks' },
                { n: -7, label: '−1 week' },
            ],
        };
    },

    computed: {
        wc() {
            return wcLookups(this.board.workcentres);
        },
        totalSeconds() {
            return this.chips.reduce((s, c) => s + (c.planned_seconds || 0), 0);
        },
        jobCount() {
            const ids = {};
            this.chips.forEach(c => { ids[c.quote_id || ('t' + c.id)] = true; });
            return Object.keys(ids).length;
        },
        dateTarget() {
            if (this.mode === 'date') return this.date ? { scheduled_date: this.date } : null;
            if (this.mode === 'days') return this.days ? { days: this.days } : null;
            return {};
        },
        personTarget() {
            return this.personMode === 'set' && this.userId ? { user_id: this.userId } : {};
        },
        hasTarget() {
            if (this.dateTarget === null) return false;
            return Object.keys(this.dateTarget).length > 0 || Object.keys(this.personTarget).length > 0;
        },
        target() {
            return Object.assign({}, this.dateTarget || {}, this.personTarget);
        },
        canMove() {
            return this.hasTarget && this.preview && !this.previewing && this.preview.moved.length > 0;
        },
        movedById() {
            const m = {};
            ((this.preview && this.preview.moved) || []).forEach(r => { m[r.slot_id] = r; });
            return m;
        },
        refusedById() {
            const m = {};
            ((this.preview && this.preview.refused) || []).forEach(r => { m[r.slot_id] = r; });
            return m;
        },
        unchangedById() {
            const m = {};
            ((this.preview && this.preview.unchanged) || []).forEach(r => { m[r.slot_id] = r; });
            return m;
        },
        oosCount() {
            return ((this.preview && this.preview.refused) || []).filter(r => /out of sequence/i.test(r.reason)).length;
        },
        pastCount() {
            return ((this.preview && this.preview.moved) || []).filter(m => m.backdated).length;
        },
        targetLabel() {
            const parts = [];
            if (this.mode === 'date' && this.date) parts.push(fmtDay(this.date));
            if (this.mode === 'days' && this.days) {
                const q = this.quick.find(x => x.n === this.days);
                parts.push(q ? q.label : ((this.days > 0 ? '+' : '') + this.days + ' days'));
            }
            if (this.personMode === 'set' && this.userId) parts.push(this.personName(this.userId));
            return parts.join(' · ');
        },
    },

    watch: {
        mode() { this.schedulePreview(); },
        date() { this.schedulePreview(); },
        days() { this.schedulePreview(); },
        personMode() { this.schedulePreview(); },
        userId() { this.schedulePreview(); },
        allowOos() { this.schedulePreview(); },
        chips() {
            if (!this.chips.length) { this.$emit('close'); return; }
            this.schedulePreview();
        },
    },

    /* Opened by a refused People-board drop: arrive already pointed at the
       target that was refused, so the first thing Frances sees is why. */
    created() {
        const p = this.preset;
        if (!p) return;
        if (p.scheduled_date) {
            this.mode = 'date';
            this.date = p.scheduled_date;
        } else if (p.days) {
            this.mode = 'days';
            this.days = p.days;
        } else {
            this.mode = 'keep';
        }
        if (p.user_id) {
            this.personMode = 'set';
            this.userId = p.user_id;
        }
        this.schedulePreview();
    },

    beforeDestroy() {
        if (this._previewTimer) clearTimeout(this._previewTimer);
    },

    methods: {
        fmtSecs,
        fmtDay,

        personName(userId) {
            const p = (this.board.people || []).find(x => x.id === userId);
            return p ? p.name : '?';
        },
        setDays(n) {
            this.mode = 'days';
            this.days = n;
        },

        payload(dryRun) {
            return {
                slot_ids: this.chips.map(c => c.id),
                target: this.target,
                allow_out_of_sequence: this.allowOos,
                dry_run: dryRun,
            };
        },

        schedulePreview() {
            if (this._previewTimer) clearTimeout(this._previewTimer);
            if (!this.hasTarget) { this.preview = null; this.previewing = false; return; }
            this.previewing = true;
            this._previewTimer = setTimeout(() => this.runPreview(), 250);
        },
        runPreview() {
            const seq = this._previewSeq = (this._previewSeq || 0) + 1;
            window.axios.post('/planner/slots/move', this.payload(true))
                .then(res => {
                    if (seq !== this._previewSeq) return;
                    this.error = null;
                    this.preview = {
                        moved: res.data.moved || [],
                        refused: res.data.refused || [],
                        unchanged: res.data.unchanged || [],
                    };
                })
                .catch(err => {
                    if (seq !== this._previewSeq) return;
                    this.preview = null;
                    this.error = errMsg(err);
                })
                .then(() => { if (seq === this._previewSeq) this.previewing = false; });
        },

        doMove() {
            if (!this.canMove || this.busy) return;
            this.error = null;
            this.$emit('move', this.payload(false), this.targetLabel);
        },
    },
};
</script>

<style scoped>
/* drawer shell — same as ChipDrawer (its styles are scoped, so restated) */
.pl-drawer-overlay{position:fixed; inset:0; background:rgba(11,11,11,.28); z-index:80;
    display:flex; justify-content:flex-end}
.pl-drawer{width:420px; max-width:94vw; height:100%; overflow-y:auto;
    background:#fcfcfb; box-shadow:-8px 0 30px rgba(11,11,11,.2);
    font:15px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif; color:#0b0b0b}
.pl-drawer-hd{padding:16px 18px 13px; border-bottom:1px solid #e1e0d9; background:#f4f3f0; position:relative}
.pl-drawer-hd::before{content:""; position:absolute; left:0; top:0; bottom:0; width:6px; background:#0ca30c}
.pl-drawer-wcb{display:inline-flex; align-items:center; gap:6px; font-size:11.5px; font-weight:800;
    color:#0ca30c; letter-spacing:.03em; text-transform:uppercase}
.pl-drawer-hd h3{font-size:17px; font-weight:800; margin:4px 0 2px}
.pl-drawer-qln{font-size:13px; color:#52514e; font-weight:600}
.pl-drawer-close{position:absolute; right:12px; top:12px; border:1px solid #e1e0d9; background:#fcfcfb;
    width:32px; height:32px; border-radius:8px; font-weight:800; color:#52514e; cursor:pointer}
.pl-drawer-error{margin:12px 16px 0; background:#f9e4e4; border:1px solid #d03b3b; color:#7c1d1d;
    border-radius:8px; padding:8px 11px; font-size:13px; font-weight:600}
.pl-drawer-sec{padding:13px 18px; border-bottom:1px solid #e1e0d9}
.pl-drawer-sec h4{font-size:11.5px; letter-spacing:.06em; text-transform:uppercase; color:#898781; font-weight:800; margin:0 0 9px}
.pl-dbtn{display:flex; align-items:center; justify-content:center; gap:9px; width:100%;
    border:1px solid #e1e0d9; background:#f9f9f7; border-radius:10px;
    padding:11px 12px; font:inherit; font-weight:700; font-size:14px; color:#0b0b0b; margin-bottom:7px; cursor:pointer}
.pl-dbtn:disabled{opacity:.55; cursor:default}
.pl-dbtn-dark{background:#0b0b0b; border-color:#0b0b0b; color:#fff}
.pl-dinput{border:1px solid #e1e0d9; border-radius:8px; padding:7px 10px; background:#fcfcfb;
    font:inherit; font-weight:650; font-size:14px; min-width:0}
.pl-dinput:disabled{opacity:.5}
.pl-dnote{font-size:12.5px; color:#52514e; background:#fdf3d9; border-radius:8px; padding:8px 10px; line-height:1.4; margin-top:4px}
.pl-bm-undo-note{background:#f4f3f0}
.pl-i{display:inline-block; width:15px; height:15px; flex:none; margin-top:3px; color:var(--plwc, #898781)}

/* the list of chips in hand */
.pl-bm-list{max-height:34vh; overflow-y:auto; padding-top:10px; padding-bottom:10px}
.pl-bm-row{display:flex; align-items:flex-start; gap:8px; padding:6px 0 6px 8px; border-left:4px solid var(--plwc, #898781);
    border-bottom:1px solid #eeede8; font-size:13px; line-height:1.35}
.pl-bm-row:last-child{border-bottom:0}
.pl-bm-row-refused{background:#fdf3d9}
.pl-bm-row-unchanged{opacity:.6}
.pl-bm-cx{flex:1; min-width:0}
.pl-bm-r1{display:flex; align-items:baseline; gap:8px}
.pl-bm-r1 b{flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap}
.pl-bm-mt{color:#52514e; font-size:11.5px; font-weight:700; white-space:nowrap}
.pl-bm-r2{color:#52514e; font-size:12px; font-weight:600}
.pl-bm-why{color:#7c1d1d; font-size:12px; font-weight:650; margin-top:2px}
.pl-bm-why-soft{color:#898781}
.pl-bm-past{font-size:9px; font-weight:800; letter-spacing:.03em; color:#8a6100; background:#fdf3d9; border-radius:10px; padding:0 5px; margin-left:4px; vertical-align:1px}
.pl-bm-x{border:0; background:transparent; color:#898781; font-size:18px; line-height:1; padding:0 6px; cursor:pointer; flex:none}
.pl-bm-x:hover{color:#d03b3b}

/* option rows */
.pl-bm-opt{display:flex; align-items:center; gap:8px; margin-bottom:8px; font-weight:650; font-size:14px; flex-wrap:wrap; cursor:pointer}
.pl-bm-opt > span{min-width:118px}
.pl-bm-opt input[type=radio]{margin:0; width:16px; height:16px}
.pl-bm-date{flex:1}
.pl-bm-person{flex:1}
.pl-bm-days{width:68px}
.pl-bm-quick{display:flex; gap:5px; flex-wrap:wrap; flex:1; align-items:center}
.pl-bm-q{border:1px solid #e1e0d9; background:#f9f9f7; border-radius:20px; padding:4px 10px; font:inherit; font-weight:700; font-size:12.5px; cursor:pointer}
.pl-bm-q.on{background:#0b0b0b; border-color:#0b0b0b; color:#fff}
.pl-bm-sub{color:#898781; font-weight:600; font-size:12px}

/* preview */
.pl-bm-prev{border-radius:8px; padding:8px 10px; font-size:13.5px; font-weight:650; margin-bottom:6px}
.pl-bm-prev-ok{background:#e4f6e4; color:#0b5d0b}
.pl-bm-prev-warn{background:#fdf3d9; color:#7c1d1d}
.pl-bm-prev-soft{background:#f4f3f0; color:#52514e}
.pl-bm-force{display:flex; gap:8px; align-items:flex-start; font-size:13px; font-weight:650; color:#52514e; margin-top:4px; cursor:pointer}
.pl-bm-force input{margin-top:3px}
</style>
